/* eslint-disable no-unused-vars */
import { useBreakpointValue, Flex, Text } from '@chakra-ui/react';
import FinishSetupIntent from 'components/stripe/finish-setup-intent';
import Payment from 'components/stripe/payment';
import { useEffect, useState, useRef } from 'react';
import { useSelector } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useRequestSetupIntentMutation } from 'redux/api/ciosUsersApi/ciosUsersApi';
import {
  selectPaymentMode,
  selectCoverageSelection,
} from 'redux/features/plans/plansSlice';
import {
  planPaymentMode,
  coverageTypes,
  backgroundTypeEnum,
} from 'common/utils/enums';
import { isEmpty } from 'common/utils/isEmpty';
import useUserTracker from 'common/utils/user-tracker/useUserTracker';
import ContactCard from 'components/shared/contact-card';
import PaymentSummaryCard from 'components/plans/payment-summary-card';
import ApplyButtons from 'components/apply-shared/apply-buttons';
import ApplyTitle from 'components/apply-shared/apply-title';
import ApplyContent from 'components/apply-shared/apply-content';
import { selectUser } from 'redux/features/auth/authSlice';
import EmptyPage from 'common/components/empty-page';
import { FaCreditCard } from 'react-icons/fa6';
import { ApplyFormLayout } from 'common/layouts/apply-form';
import { RefundPolicyLink } from 'components/plans/refund-policy-modal';

const PlanStepSetPayment = ({ onBackClick, onNextClick, planData }) => {
  const { trackPageLoad, trackCustomEvent } = useUserTracker();

  useEffect(() => {
    trackPageLoad('PlanStepSetPayment');
  }, []);

  // STATES
  const [clientSecret, setSecret] = useState();
  const [isPaymentSuccess, setPaymentSuccess] = useState(false);
  const [localSetupIntentID, setLocalSetupIntentID] = useState(null); // New state for non-redirect flow
  const [isPaymentElementLoading, setIsPaymentElementLoading] = useState(true); // To track Payment Element loading

  // Ref for external form submit
  const paymentRef = useRef(null);

  // Determine if we're on md+ breakpoint
  const isDesktop = useBreakpointValue({ base: false, md: true });

  // HOOKS
  const [requestSetupIntent] = useRequestSetupIntentMutation();
  const [searchParams, setSearchParams] = useSearchParams();
  const setupIntent = searchParams.get('setup_intent');

  // Retrieve user details
  const userDetails = useSelector(selectUser);

  // Retreive payment and coverage details
  const coverageSelection = useSelector(selectCoverageSelection);
  const brand = planData?.property?.brand;
  const isDarkBg = brand?.backgroundType === backgroundTypeEnum.BACKGROUND_DARK;

  useEffect(() => {
    console.log(coverageSelection)
  }, [coverageSelection])

  // SIDE EFFECTS
  useEffect(() => {
    requestSetupIntent()
      .unwrap()
      .then((response) => {
        setSecret(response?.data?.clientSecret);
      })
      .catch((error) => {
        toast.error(error?.message || 'Failed to setup intent!');
      });
  }, [requestSetupIntent]);

  useEffect(() => {
    if (clientSecret && isEmpty(setupIntent)) {
      setIsPaymentElementLoading(true); // Start loading indication when clientSecret is available and not in setupIntent flow
    } else {
      setIsPaymentElementLoading(false); // Reset if conditions change
    }
  }, [clientSecret, setupIntent]);

  const handlePaymentElementReady = () => {
    setIsPaymentElementLoading(false);
  };

  const handleSetupSuccess = (id) => {
    setLocalSetupIntentID(id);
    // FinishSetupIntent will be rendered based on localSetupIntentID
  };

  return (
    <div className='flex flex-col w-full h-full'>
      <ApplyTitle
        title='Purchase Coverage'
        subtitle='Add your payment method of choice for your coverage plan.'
        buttons={
          <ApplyButtons
            loading={
              isPaymentElementLoading ||
              // Disable while payment is being processed (redirect or non-redirect flow)
              ((!isEmpty(setupIntent) || localSetupIntentID) &&
                !isPaymentSuccess)
            }
            isValid // Always valid, otherwise it will be disabled
            handleSubmit={() => paymentRef.current?.submit()}
            onBackClick={onBackClick}
            onNextClick={() => paymentRef.current?.submit()}
            disableBack={!isEmpty(setupIntent)}
            disableNext={isDesktop}
            nextTitle='Complete Payment'
          />
        }
      />

      {/* main content */}
      <ApplyContent>
        {planData?.property && (
          <ApplyFormLayout
            className={`p-6 rounded-lg border relative overflow-clip ${
              brand ? 'border-gray-200' : 'border-gray-200 bg-gray-50'
            }`}
            style={brand?.bgColor ? { backgroundColor: brand.bgColor } : {}}
          >
            <Flex className='z-10 relative flex-col sm:flex-row gap-6 sm:gap-8 col-span-2'>
              {brand && (
                <img
                  src={isDarkBg && brand?.logoAlt ? brand?.logo : brand?.logo}
                  alt={planData?.property?.name}
                  className='max-w-[140px] max-h-[50px] object-contain'
                />
              )}
              <Flex className='flex-col gap-1'>
                <Text
                  className={`font-medium text-lg ${
                    isDarkBg ? 'text-white' : 'text-black'
                  }`}
                >
                  ReLease Coverage at {planData?.property?.name}
                </Text>
                <Text
                  className={`text-base ${isDarkBg ? 'text-white opacity-60' : 'text-black opacity-60'}`}
                >
                  {planData?.property?.address?.streetAddress1},{' '}
                  {planData?.property?.address?.city},{' '}
                  {planData?.property?.address?.state}{' '}
                  {planData?.property?.address?.postalCode}
                </Text>
              </Flex>
            </Flex>
            {brand?.backgroundHeroImage && (
              <img
                src={brand?.backgroundHeroImage}
                alt={planData?.property?.name}
                className='absolute inset-0 opacity-15 z-0 object-cover'
              />
            )}
          </ApplyFormLayout>
        )}
        <div className='grid grid-cols-1 md:grid-cols-[2fr_1fr] gap-8 w-full grid-flow-dense justify-items-center'>
          {/* Payment summary card */}
          <div className='w-full col-start-1 md:col-start-2 flex flex-col gap-4 md:sticky md:top-0 md:self-start'>
            <PaymentSummaryCard
              onClick={
                isDesktop ? () => paymentRef.current?.submit() : undefined
              }
              isLoading={
                isDesktop && (isPaymentElementLoading || !clientSecret)
              }
              plan={planData}
              coverageSelection={coverageSelection}
            />
          </div>

          {/* Payment component */}
          <div className='w-full relative'>
            {!clientSecret ? (
              <EmptyPage
                isLoading={true}
                title='Loading Payment'
                subtitle='Please wait while we load your payment method.'
              />
            ) : !isEmpty(setupIntent) || localSetupIntentID ? ( // Check both search param and local state
              <FinishSetupIntent
                removeSetupIntent={() => {
                  searchParams.delete('setup_intent');
                  setSearchParams(searchParams, { replace: true });
                  setLocalSetupIntentID(null); // Also clear local state
                }}
                onNextClick={onNextClick}
                setPaymentSuccess={setPaymentSuccess}
                setupIntentID={setupIntent || localSetupIntentID} // Use ID from either source
                planData={planData}
              />
            ) : (
              // clientSecret is available, and not in setupIntent flow
              <>
                {isPaymentElementLoading && (
                  <EmptyPage
                    PageIcon={FaCreditCard}
                    isLoading={isPaymentElementLoading}
                    title='Loading Payment'
                    subtitle='Please wait while we load your payment method.'
                  />
                )}
                <div
                  style={{
                    display: isPaymentElementLoading ? 'none' : 'block',
                  }}
                >
                  <Payment
                    clientSecret={clientSecret}
                    returnUrl={`${window.location.href}`}
                    ref={paymentRef}
                    showButton={false}
                    onElementReady={handlePaymentElementReady}
                    onSetupSuccess={handleSetupSuccess} // Pass the handler
                  >
                    <div className='flex flex-col gap-3 text-sm text-gray-500 md:hidden'>
                      <div className='mt-3'>
                        <span className='font-semibold'>
                          By clicking Complete Payment, you give permission to
                          ReLease to charge your provided payment method for the
                          amounts above
                        </span>
                        , and agree to ReLease's{' '}
                        <a
                          href='https://www.releaserent.com/legal/terms-of-service'
                          target='_blank'
                          rel='noopener noreferrer'
                          className='hover:underline'
                        >
                          Terms of Service
                        </a>{' '}
                        and{' '}
                        <a
                          href='https://www.releaserent.com/legal/privacy-policy'
                          target='_blank'
                          rel='noopener noreferrer'
                          className='hover:underline'
                        >
                          Privacy Policy
                        </a>
                        . Based on the coverage selected above, you agree to the{' '}
                        <a
                          href='https://www.releaserent.com/legal/release-pre-lease-terms-conditions'
                          target='_blank'
                          rel='noopener noreferrer'
                          className='hover:underline'
                        >
                          ReLease Pre-Lease Coverage
                        </a>{' '}
                        and the{' '}
                        <a
                          href='https://www.releaserent.com/legal/release-mid-lease-terms-conditions'
                          target='_blank'
                          rel='noopener noreferrer'
                          className='hover:underline'
                        >
                          ReLease Mid-Lease Coverage
                        </a>
                        {' '}Terms and Conditions.
                      </div>
                      <RefundPolicyLink>
                        Learn more about the ReLease Refund Policy
                      </RefundPolicyLink>
                    </div>
                  </Payment>
                </div>
              </>
            )}
            <ContactCard
              title='Have Any Questions?'
              // className='hidden md:block'
              className='mt-8 border-t border-gray-200 pt-6'
              border={false}
            >
              Contact our member support team for help with your plan using the
              information below.
            </ContactCard>
          </div>
        </div>

        {/* Footer */}
      </ApplyContent>
    </div>
  );
};

export default PlanStepSetPayment;
